import { Query } from "@cubejs-client/core";

import { AppThunk } from "appThunk";
import { apiGet } from "modules/helpers/api/apiSlice";
import { cubeLoadParallel } from "modules/helpers/cube/cubeSlice";
import { logError } from "modules/helpers/logger/loggerSlice";

import { StoreCategory } from "./storeCategory";
import { SpendCategory } from "./spendCategory";

export enum ScoreField {
    Overall = "overallScore",
    Demographics = "demographicsScore",
    Spend = "spendScore",
    AreaHealth = "areaHealthScore",
    Footfall = "footfallScore",
    Retailers = "retailersScore"
}

export class StaticRetailCentre {
    public readonly id: number;
    public readonly code: string;
    public readonly isInRetailCentre: boolean;
    public readonly regionCode: string;
    public readonly regionName: string;
    public readonly localAuthorityCode: string;
    public readonly latitude: number;
    public readonly longitude: number;

    constructor(
        id: number,
        code: string,
        isInRetailCentre: boolean,
        regionCode: string,
        regionName: string,
        localAuthorityCode: string,
        latitude: number,
        longitude: number
    ) {
        this.id = id;
        this.code = code;
        this.isInRetailCentre = isInRetailCentre;
        this.regionCode = regionCode;
        this.regionName = regionName;
        this.localAuthorityCode = localAuthorityCode;
        this.latitude = latitude;
        this.longitude = longitude;
    }
}

interface DynamicRetailCentre {
    id: number,
    affluenceCentile: number,
    ageCentile: number,
    childrenCentile: number,
    diversityCentile: number,
    urbanicityCentile: number,
    areaHealthCentile: number,
    storeCountCentile: number,
    brandPositioningCentile: number,
    nullBrandPositioning: boolean,
    yoYNetOpeningsPercentage?: number,
    footfallCentile: number,
    footfallCountCentile: number,
    meanSpendCentile: number
}

export class RetailCentre extends StaticRetailCentre {
    public readonly storeCategoryId: number;
    public readonly affluenceCentile: number;
    public readonly ageCentile: number;
    public readonly childrenCentile: number;
    public readonly diversityCentile: number;
    public readonly urbanicityCentile: number;
    public readonly spendCentile: number;
    public readonly areaHealthCentile: number;
    public readonly numberOfStoresCentile: number;
    public readonly brandPositioningCentile: number;
    public readonly nullBrandPositioning: boolean;
    public readonly emptyAreaHealth: boolean;
    public readonly footfallCentile: number;
    public readonly pitchType?: string;

    constructor(
        staticRetailCentre: StaticRetailCentre,
        storeCategoryId: number,
        affluenceCentile: number,
        ageCentile: number,
        childrenCentile: number,
        diversityCentile: number,
        urbanicityCentile: number,
        spendCentile: number,
        areaHealthCentile: number,
        numberOfStoresCentile: number,
        brandPositioningCentile: number,
        nullBrandPositioning: boolean,
        emptyAreaHealth: boolean,
        footfallCentile: number,
        pitchType?: string
    ) {
        super(
            staticRetailCentre.id,
            staticRetailCentre.code,
            staticRetailCentre.isInRetailCentre,
            staticRetailCentre.regionCode,
            staticRetailCentre.regionName,
            staticRetailCentre.localAuthorityCode,
            staticRetailCentre.latitude,
            staticRetailCentre.longitude
        );
        this.storeCategoryId = storeCategoryId;
        this.affluenceCentile = affluenceCentile;
        this.ageCentile = ageCentile;
        this.childrenCentile = childrenCentile;
        this.diversityCentile = diversityCentile;
        this.urbanicityCentile = urbanicityCentile;
        this.spendCentile = spendCentile;
        this.areaHealthCentile = areaHealthCentile;
        this.numberOfStoresCentile = numberOfStoresCentile;
        this.brandPositioningCentile = brandPositioningCentile;
        this.nullBrandPositioning = nullBrandPositioning;
        this.emptyAreaHealth = emptyAreaHealth;
        this.footfallCentile = footfallCentile;
        this.pitchType = pitchType;
    }
}

export class RetailCentreWithScores extends RetailCentre {
    public readonly overallScore: number;
    public readonly demographicsScore: number;
    public readonly spendScore: number;
    public readonly areaHealthScore: number;
    public readonly footfallScore: number;
    public readonly retailersScore: number;

    constructor(
        retailCentre: RetailCentre,
        overallScore: number,
        demographicsScore: number,
        spendScore: number,
        areaHealthScore: number,
        footfallScore: number,
        retailersScore: number
    ) {
        super(
            retailCentre,
            retailCentre.storeCategoryId,
            retailCentre.affluenceCentile,
            retailCentre.ageCentile,
            retailCentre.childrenCentile,
            retailCentre.diversityCentile,
            retailCentre.urbanicityCentile,
            retailCentre.spendCentile,
            retailCentre.areaHealthCentile,
            retailCentre.numberOfStoresCentile,
            retailCentre.brandPositioningCentile,
            retailCentre.nullBrandPositioning,
            retailCentre.emptyAreaHealth,
            retailCentre.footfallCentile,
            retailCentre.pitchType
        );
        this.overallScore = overallScore;
        this.demographicsScore = demographicsScore;
        this.spendScore = spendScore;
        this.areaHealthScore = areaHealthScore;
        this.footfallScore = footfallScore;
        this.retailersScore = retailersScore;
    }

    getRagScore = (scoreField: ScoreField): number => {
        const alignmentScore = this[scoreField];
        let threshold = 3;
        for (let i = 0; i <= 5; i++) {
            if (alignmentScore < threshold) {
                return i;
            }
            threshold += 1.5;
        }
        return 5;
    };
}

export const loadStaticRetailCentres = (): AppThunk<Promise<Map<number, StaticRetailCentre>>> => async (dispatch) => {
    try {
        const downloadUrlResponse = await dispatch(apiGet("/api/customer/tools/location/retail-centres"));
        const downloadUrl = downloadUrlResponse.data.url;
        const staticFileResponse = await dispatch(apiGet(downloadUrl));
        const retailCentresFromFile = staticFileResponse?.data ?? [];
        const staticRetailCentres = new Map<number, StaticRetailCentre>();
        retailCentresFromFile.forEach((retailCentreFromFile: any) => {
            const retailCentreId = Number(retailCentreFromFile.id);
            const staticRetailCentre = new StaticRetailCentre(
                retailCentreId,
                String(retailCentreFromFile.code),
                Boolean(Number(retailCentreFromFile.isInRetailCentre)),
                String(retailCentreFromFile.regionCode),
                String(retailCentreFromFile.regionName),
                String(retailCentreFromFile.localAuthorityCode),
                Number(retailCentreFromFile.latitude),
                Number(retailCentreFromFile.longitude));
            staticRetailCentres.set(retailCentreId, staticRetailCentre);
        });
        return staticRetailCentres;
    } catch (error) {
        dispatch(logError("Error loading StaticRetailCentres.", error));
        throw error;
    }
};

export const loadRetailCentres = (
    staticRetailCentres: Map<number, StaticRetailCentre>,
    targetStoreCategory: StoreCategory | undefined,
    targetSpendCategories: SpendCategory[],
    enableSpendNew: boolean,
    enableFootfallNew: boolean,
    scenarioCatchmentAccountId: string,
    enableRetailCentresDirectQuery: boolean
): AppThunk<Promise<RetailCentre[]>> => async (dispatch) => {
    if (!targetStoreCategory || staticRetailCentres.size === 0) {
        return [];
    }

    try {
        const targetStoreCategoryId = targetStoreCategory.id;
        const targetSpendCategoryIds = targetSpendCategories.map(spendCategory => String(spendCategory.id));
        const retailCentres = enableRetailCentresDirectQuery
            ? await dispatch(loadRetailCentresFromApi(staticRetailCentres, targetStoreCategoryId, targetSpendCategoryIds, scenarioCatchmentAccountId, enableFootfallNew))
            : await dispatch(loadRetailCentresFromCube(staticRetailCentres, targetStoreCategoryId, targetSpendCategoryIds, enableSpendNew, enableFootfallNew, scenarioCatchmentAccountId));
        return retailCentres;
    } catch (error) {
        dispatch(logError("Error loading RetailCentres.", error));
        throw error;
    }
};

const loadRetailCentresFromCube = (
    staticRetailCentres: Map<number, StaticRetailCentre>,
    targetStoreCategoryId: number,
    targetSpendCategoryIds: string[],
    enableSpendNew: boolean,
    enableFootfallNew: boolean,
    scenarioCatchmentAccountId: string
): AppThunk<Promise<RetailCentre[]>> => async (dispatch) => {
    const footfallCentileName = enableFootfallNew ? "LocationBenchmarkMetrics.FootfallCountCentile" : "LocationBenchmarkMetrics.FootfallCentile";
    const query: Query = {
        dimensions: [
            "LocationBenchmarkMetrics.RetailCentreID",
            "LocationBenchmarkMetrics.AffluenceCentile",
            "LocationBenchmarkMetrics.AgeCentile",
            "LocationBenchmarkMetrics.ChildrenCentile",
            "LocationBenchmarkMetrics.DiversityCentile",
            "LocationBenchmarkMetrics.UrbanicityCentile",
            "LocationBenchmarkMetrics.AreaHealthCentile",
            "LocationBenchmarkMetrics.StoreCountCentile",
            "LocationBenchmarkMetrics.BrandPositioningCentile",
            "LocationBenchmarkMetrics.BrandPositioningScore",
            "LocationBenchmarkMetrics.YoYNetOpeningsPercentage",
            footfallCentileName
        ],
        filters: [{
            or: [{
                member: "LocationBenchmarkMetrics.Client_ID",
                operator: "equals",
                values: [scenarioCatchmentAccountId]
            }, {
                member: "LocationBenchmarkMetrics.Client_ID",
                operator: "notSet",
            }]
        }, {
            member: "LocationBenchmarkMetrics.StoreCategory_ID",
            operator: "equals",
            values: [String(targetStoreCategoryId)]
        }],
        segments: [
            "LocationBenchmarkMetrics.Scenario",
        ],
        order: {
            "LocationBenchmarkMetrics.RetailCentreID": "asc",
        }
    };

    if (enableSpendNew) {
        query.measures = ["LocationBenchmarkSpendMetrics.MeanSpendCentile"];
        query.filters?.push({
            member: "LocationBenchmarkSpendMetrics.SpendCategory_ID",
            operator: "equals",
            values: targetSpendCategoryIds
        });
    }

    const resultSet = await dispatch(cubeLoadParallel(query, staticRetailCentres.size));

    const retailCentres: RetailCentre[] = [];
    resultSet.forEach(item => {
        const retailCentreId = Number(item["LocationBenchmarkMetrics.RetailCentreID"] ?? 0);
        const staticRetailCentre = staticRetailCentres.get(retailCentreId);
        if (staticRetailCentre) {
            const retailCentre = new RetailCentre(
                staticRetailCentre,
                Number(targetStoreCategoryId),
                Number(item["LocationBenchmarkMetrics.AffluenceCentile"] ?? 0),
                Number(item["LocationBenchmarkMetrics.AgeCentile"] ?? 0),
                Number(item["LocationBenchmarkMetrics.ChildrenCentile"] ?? 0),
                Number(item["LocationBenchmarkMetrics.DiversityCentile"] ?? 0),
                Number(item["LocationBenchmarkMetrics.UrbanicityCentile"] ?? 0),
                enableSpendNew ? Number(item["LocationBenchmarkSpendMetrics.MeanSpendCentile"] ?? 0) : 0,
                Number(item["LocationBenchmarkMetrics.AreaHealthCentile"] ?? 0),
                Number(item["LocationBenchmarkMetrics.StoreCountCentile"] ?? 0),
                Number(item["LocationBenchmarkMetrics.BrandPositioningCentile"]), // ToDo: Allow null value from SQL/Cube
                Boolean(item["LocationBenchmarkMetrics.BrandPositioningScore"] === null),
                Boolean(item["LocationBenchmarkMetrics.YoYNetOpeningsPercentage"] === null),
                Number(item[footfallCentileName] ?? 0)
            );
            retailCentres.push(retailCentre);
        }
    });
    return retailCentres;
};

const loadRetailCentresFromApi = (
    staticRetailCentres: Map<number, StaticRetailCentre>,
    targetStoreCategoryId: number,
    targetSpendCategoryIds: string[],
    scenarioCatchmentAccountId: string,
    enableFootfallNew: boolean
): AppThunk<Promise<RetailCentre[]>> => async (dispatch) => {
    const spendCategoryIds = targetSpendCategoryIds.join(",");
    const url = `/api/customer/tools/location/dynamic-retail-centres?accountId=${scenarioCatchmentAccountId}&storeCategoryId=${targetStoreCategoryId}&spendCategoryIds=${spendCategoryIds}`;
    const response = await dispatch(apiGet(url, false));
    const dynamicRetailCentres = response.data.dynamicRetailCentres as DynamicRetailCentre[];

    const retailCentres: RetailCentre[] = [];
    dynamicRetailCentres.forEach(dynamicRetailCentre => {
        const retailCentreId = dynamicRetailCentre.id;
        const staticRetailCentre = staticRetailCentres.get(retailCentreId);
        if (staticRetailCentre) {
            const retailCentre = new RetailCentre(
                staticRetailCentre,
                targetStoreCategoryId,
                dynamicRetailCentre.affluenceCentile,
                dynamicRetailCentre.ageCentile,
                dynamicRetailCentre.childrenCentile,
                dynamicRetailCentre.diversityCentile,
                dynamicRetailCentre.urbanicityCentile,
                dynamicRetailCentre.meanSpendCentile,
                dynamicRetailCentre.areaHealthCentile,
                dynamicRetailCentre.storeCountCentile,
                dynamicRetailCentre.brandPositioningCentile,
                dynamicRetailCentre.nullBrandPositioning,
                dynamicRetailCentre.yoYNetOpeningsPercentage === undefined || dynamicRetailCentre.yoYNetOpeningsPercentage === null,
                enableFootfallNew ? dynamicRetailCentre.footfallCountCentile : dynamicRetailCentre.footfallCentile
            );
            retailCentres.push(retailCentre);
        }
    });
    return retailCentres;
};
